import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, Wallet, TrendingUp, AlertCircle, School, CalendarRange } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card, StatCard } from '../../components/school/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { formatXOF } from '../../lib/utils';
import { toast } from 'sonner';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym || '\u2014';
  const [y, m] = ym.split('-');
  const idx = Number(m) - 1;
  return `${MONTH_LABELS[idx] || m} ${y}`;
};

export default function FeesDashboardPage() {
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      setData(await schoolApi.feesDashboard(orgId, { academic_year_id: selectedYearId }));
    } catch {
      toast.error('Failed to load fees dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, selectedYearId]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || { billed: 0, collected: 0, outstanding: 0, invoice_count: 0, receipt_count: 0 };
  const byClass = data?.by_class || [];
  const byMonth = data?.by_month || [];
  const hasData = (summary.invoice_count || 0) > 0 || byClass.length > 0 || byMonth.length > 0;
  const collectionRate = summary.billed > 0 ? Math.round((summary.collected / summary.billed) * 100) : 0;

  const Money = ({ value, className = '' }) => (
    <span className={className}>{formatXOF(value)} <span className="text-xs text-muted-foreground">FCFA</span></span>
  );

  return (
    <div className="space-y-6" data-testid="fees-dashboard-page">
      <PageHeader icon={BarChart3} title="Fees Dashboard" subtitle="Collections summary for the selected academic year" />

      {loading ? (
        <LoadingState rows={6} />
      ) : !hasData ? (
        <EmptyState icon={BarChart3} title="No fees data yet" description="Create invoices and register receipts to see collection insights here." />
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Total Billed" value={`${formatXOF(summary.billed)} FCFA`} icon={TrendingUp} accent="students" testId="stat-billed" />
            <StatCard label="Total Collected" value={`${formatXOF(summary.collected)} FCFA`} icon={Wallet} accent="teachers" testId="stat-collected" />
            <StatCard label="Outstanding" value={`${formatXOF(summary.outstanding)} FCFA`} icon={AlertCircle} accent="classes" testId="stat-outstanding" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-4" data-testid="meta-collection-rate">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Collection Rate</div>
              <div className="mt-1 text-2xl font-semibold">{collectionRate}%</div>
              <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(collectionRate, 100)}%` }} />
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Invoices</div>
              <div className="mt-1 text-2xl font-semibold" data-testid="meta-invoice-count">{summary.invoice_count}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Receipts</div>
              <div className="mt-1 text-2xl font-semibold" data-testid="meta-receipt-count">{summary.receipt_count}</div>
            </Card>
          </div>

          {/* Breakdown toggle */}
          <Tabs defaultValue="class" className="space-y-4">
            <TabsList data-testid="dashboard-view-tabs">
              <TabsTrigger value="class" data-testid="tab-by-class"><School className="w-4 h-4 mr-1.5" /> By Class</TabsTrigger>
              <TabsTrigger value="month" data-testid="tab-by-month"><CalendarRange className="w-4 h-4 mr-1.5" /> By Month</TabsTrigger>
            </TabsList>

            <TabsContent value="class">
              <Card className="overflow-x-auto">
                {byClass.length === 0 ? (
                  <div className="p-6"><EmptyState icon={School} title="No class data" /></div>
                ) : (
                  <table className="w-full text-sm" data-testid="by-class-table">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="p-3">Class</th>
                        <th className="p-3 text-right">Invoices</th>
                        <th className="p-3 text-right">Billed</th>
                        <th className="p-3 text-right">Collected</th>
                        <th className="p-3 text-right">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byClass.map((c) => (
                        <tr key={c.class_id || c.class_name} className="border-b hover:bg-muted/40" data-testid={`class-row-${c.class_id || 'none'}`}>
                          <td className="p-3 font-medium">{c.class_name || 'Unassigned'}</td>
                          <td className="p-3 text-right">{c.invoice_count}</td>
                          <td className="p-3 text-right"><Money value={c.billed} /></td>
                          <td className="p-3 text-right text-emerald-600"><Money value={c.collected} /></td>
                          <td className="p-3 text-right text-amber-600"><Money value={c.outstanding} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/30 font-semibold">
                        <td className="p-3">Total</td>
                        <td className="p-3 text-right">{summary.invoice_count}</td>
                        <td className="p-3 text-right"><Money value={summary.billed} /></td>
                        <td className="p-3 text-right text-emerald-600"><Money value={summary.collected} /></td>
                        <td className="p-3 text-right text-amber-600"><Money value={summary.outstanding} /></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="month">
              <Card className="overflow-x-auto">
                {byMonth.length === 0 ? (
                  <div className="p-6"><EmptyState icon={CalendarRange} title="No monthly data" /></div>
                ) : (
                  <table className="w-full text-sm" data-testid="by-month-table">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="p-3">Month</th>
                        <th className="p-3 text-right">Billed</th>
                        <th className="p-3 text-right">Collected</th>
                        <th className="p-3 text-right">Net (Billed - Collected)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byMonth.map((m) => (
                        <tr key={m.month} className="border-b hover:bg-muted/40" data-testid={`month-row-${m.month}`}>
                          <td className="p-3 font-medium">{monthLabel(m.month)}</td>
                          <td className="p-3 text-right"><Money value={m.billed} /></td>
                          <td className="p-3 text-right text-emerald-600"><Money value={m.collected} /></td>
                          <td className="p-3 text-right text-amber-600"><Money value={m.outstanding} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
